import React, { useState, useEffect, useContext } from "react";
import Select from "react-select";
import axios from "axios";
import { URL, getToken, getLocalDateTime } from "../../utils";
import { UserContext } from "../../contexts/UserContext";
import { toast, ToastContainer } from "react-toastify";
import ErrorModal from "../../components/ErrorModal";
import "react-toastify/dist/ReactToastify.css";
import NumberInput from "../../components/NumberInput";
import ConditionalRender from "../../components/ConditionalRender";

// Icônes SVG
const TransfertIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    className="inline mr-1"
  >
    <path
      d="M16 5L19 8M19 8L16 11M19 8H5M8 19L5 16M5 16L8 13M5 16H19"
      stroke="#4CAF50"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DonIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    className="inline mr-1"
  >
    <path
      d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.03L12 21.35Z"
      fill="#E91E63"
    />
  </svg>
);

const EventIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    className="inline mr-1"
  >
    <path
      d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z"
      stroke="#3B82F6"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TastingIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    className="inline mr-1"
  >
    <path
      d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"
      fill="#8B5CF6"
    />
  </svg>
);

const OfferIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    className="inline mr-1"
  >
    <path
      d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
      stroke="#EC4899"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StockOutflowApp = () => {
  const [state, setState] = useState({
    movements: [],
    loading: true,
    sellPoints: [],
    articles: [],
    isSubmitting: false,
  });

  const { user } = useContext(UserContext);
  const token = getToken();
  const [errors, setErrors] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [currentMovement, setCurrentMovement] = useState(null);

  const [formData, setFormData] = useState({
    stock_item: null,
    quantite: "",
    unite: "unite",
    magasin: user.is_caissier ? user.magasin_id : null,
    magasin_receiver: null,
    type: "transfert_store",
    name_receiver: "",
    name_event: "",
    name_taster: "",
    commentaire: "",
    // Nouveaux champs pour traçabilité
    name_order_by: "", // Nom de la personne ayant demandé la sortie
    collecteur_produit: "", // Nom de la personne venant chercher le produit
  });

  // Configuration des options
  const options = React.useMemo(
    () => ({
      movementTypes: [
        { value: "transfert_store", label: "Transfert à un autre store" },
        { value: "transfert_event", label: "Transfert à un événement" },
        { value: "offre", label: "Offre" },
      ],
      units: [{ value: "unite", label: "Unité" }],
    }),
    []
  );

  useEffect(() => {
    fetchData();
    if (user.is_caissier) {
      fetchArticles(user.magasin_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true }));

      const [sellPointsResponse, movementsResponse] = await Promise.all([
        axios.get(`${URL}Magasins/magasins-list`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        user.is_caissier
          ? axios.get(
              `${URL}Declarations/liste-other-sorties/${user.magasin_id}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            )
          : axios.get(`${URL}Declarations/liste-other-sorties`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
      ]);

      setState((prev) => ({
        ...prev,
        sellPoints: sellPointsResponse.data.map((store) => ({
          value: store.id,
          label: store.nom,
        })),
        movements: movementsResponse.data,
        loading: false,
      }));
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const fetchArticles = async (magasinId) => {
    try {
      const response = await axios.get(
        `${URL}Magasins/articles-magasin/${magasinId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setState((prev) => ({
        ...prev,
        articles: response.data.stock_items.map((article) => ({
          value: article.id,
          label: article.nom,
          stock: article.quantite,
        })),
      }));
    } catch (error) {
      console.error("Error:", error);
      toast.error("Erreur lors du chargement des articles");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selectedOption, { name }) => {
    // Si l'utilisateur change d'article, réinitialiser la quantité
    if (name === "stock_item") {
      setFormData((prev) => ({
        ...prev,
        [name]: selectedOption?.value || null,
        // Réinitialiser la quantité lors du changement d'article
        quantite: "",
      }));
    } else if (name === "type") {
      // Réinitialiser les champs spécifiques selon le type
      setFormData((prev) => ({
        ...prev,
        [name]: selectedOption?.value || null,
        // Réinitialiser les champs spécifiques
        magasin_receiver: null,
        name_receiver: "",
        name_event: "",
        name_taster: "",
        name_order_by: "",
        collecteur_produit: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: selectedOption?.value || null,
        ...(name === "magasin" && {
          magasin_receiver: null,
          stock_item: null,
          quantite: "", // Réinitialiser la quantité aussi lors du changement de magasin
        }),
      }));
    }

    if (name === "magasin" && selectedOption) {
      fetchArticles(selectedOption.value);
    }
  };

  // Vérifier si le formulaire est valide pour activer/désactiver le bouton
  const isFormValid = () => {
    // Vérifie si le formulaire est valide sans afficher d'erreurs
    const errors = validateForm();
    return Object.keys(errors).length === 0;
  };

  const validateForm = () => {
    const errors = {};

    // Validation commune à tous les types
    if (!formData.stock_item) errors.stock_item = "Article requis";
    if (!formData.quantite || formData.quantite <= 0)
      errors.quantite = "Quantité invalide";
    if (!formData.commentaire || formData.commentaire.trim() === "")
      errors.commentaire = "Commentaire requis";

    // Validation des nouveaux champs obligatoires
    if (!formData.name_order_by || formData.name_order_by.trim() === "")
      errors.name_order_by = "Nom du demandeur requis";
    if (
      !formData.collecteur_produit ||
      formData.collecteur_produit.trim() === ""
    )
      errors.collecteur_produit = "Nom du collecteur requis";

    // Vérification que la quantité ne dépasse pas le stock disponible
    const selectedArticle = state.articles.find(
      (article) => article.value === formData.stock_item
    );

    if (selectedArticle && formData.quantite > selectedArticle.stock) {
      errors.quantite = `La quantité (${formData.quantite}) ne peut pas dépasser le stock disponible (${selectedArticle.stock})`;
    }

    // Validation spécifique à chaque type
    switch (formData.type) {
      case "transfert_store":
        if (!formData.magasin_receiver) {
          errors.magasin_receiver = "Magasin destinataire requis";
        }
        break;
      case "transfert_event":
        if (!formData.name_event || formData.name_event.trim() === "") {
          errors.name_event = "Nom de l'événement requis";
        }
        break;
      case "offre":
        if (!formData.name_receiver || formData.name_receiver.trim() === "") {
          errors.name_receiver = "Nom du destinataire requis";
        }
        break;
      default:
        // Pas de validation spécifique pour les autres types
        break;
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Vérifier si le formulaire est valide
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setModalOpen(true);
      toast.error("Veuillez corriger les erreurs dans le formulaire");
      return;
    }

    // Dernière vérification de stock avant soumission
    const selectedArticle = state.articles.find(
      (article) => article.value === formData.stock_item
    );

    if (selectedArticle && formData.quantite > selectedArticle.stock) {
      const stockError = {
        quantite: `La quantité (${formData.quantite}) ne peut pas dépasser le stock disponible (${selectedArticle.stock})`,
      };
      setErrors(stockError);
      setModalOpen(true);
      toast.error("Quantité supérieure au stock disponible");
      return;
    }

    setState((prev) => ({ ...prev, isSubmitting: true }));

    // Convertir les champs vides en null
    const dataToSubmit = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => {
        // Si la valeur est une chaîne vide, la convertir en null
        return [key, value === "" ? null : value];
      })
    );

    try {
      await axios.post(
        `${URL}Declarations/create-other-sorties/${formData.magasin}`,
        dataToSubmit,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Sortie enregistrée avec succès");
      await fetchData();
      // Réinitialiser le formulaire
      setFormData({
        stock_item: null,
        quantite: "",
        unite: "unite",
        magasin: user.is_caissier ? user.magasin_id : null,
        magasin_receiver: null,
        type: "transfert_store",
        name_receiver: "",
        name_event: "",
        name_taster: "",
        commentaire: "",
        // Réinitialiser les nouveaux champs
        name_order_by: "",
        collecteur_produit: "",
      });
    } catch (error) {
      console.error("Error:", error);
      const apiErrors = error.response?.data || {};
      setErrors(apiErrors);
      setModalOpen(true);
    } finally {
      setState((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  if (state.loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <>
      <ErrorModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        errors={errors}
      />
      {currentMovement && (
        <div className="fixed top-0 left-0 inset-0 z-50 py-8">
          <button
            className="absolute top-0 left-0 inset-0 bg-black bg-opacity-15 backdrop-blur-sm -z-10"
            onClick={() => {
              setCurrentMovement(null);
            }}
          ></button>
          <div className="mx-auto h-max w-[min(80%,500px)] bg-white p-4 rounded-md shadow-md space-y-4">
            <div className="flex item-center justify-between">
              <span className="flex item-center gap-2">
                <svg
                  className="w-6 h-6"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    fillRule="evenodd"
                    d="M5 5a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1h1a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1h1a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1 2 2 0 0 1 2 2v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a2 2 0 0 1 2-2ZM3 19v-7a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Zm6.01-6a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm2 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm6 0a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-10 4a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm6 0a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm2 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="capitalize">
                  {getLocalDateTime(currentMovement.date_entree)}
                </span>
              </span>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  currentMovement.type === "transfert"
                    ? "bg-green-100 text-green-800"
                    : currentMovement.type === "don"
                    ? "bg-primary-100 text-primary-800"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {currentMovement.type === "transfert" ? (
                  <TransfertIcon />
                ) : (
                  <DonIcon />
                )}
                {currentMovement.type === "transfert"
                  ? "Transfert"
                  : currentMovement.type === "don"
                  ? "Don"
                  : "Autre"}
              </span>
            </div>
            <h3>Article: {currentMovement.stock_item_name}</h3>
            <div className="space-y-4">
              <div>
                <p className="text-lg font-medium">Détails de la sortie</p>
                <ul className="capitalize p-4 rounded-md bg-gray-100 font-light space-y-1">
                  <li>
                    <strong>Unité:</strong> {currentMovement.unite}
                  </li>
                  <li>
                    <strong>Type:</strong> {currentMovement.type}
                  </li>
                  <li>
                    <strong>Quantité:</strong> {currentMovement.quantite}
                  </li>
                  {currentMovement.magasin_name && (
                    <li>
                      <strong>Magasin:</strong> {currentMovement.magasin_name}
                    </li>
                  )}
                  {currentMovement.magasin_receiver_name && (
                    <li>
                      <strong>Magasin Récepteur:</strong>{" "}
                      {currentMovement.magasin_receiver_name}
                    </li>
                  )}
                  {currentMovement.name_event && (
                    <li>
                      <strong>Événement:</strong> {currentMovement.name_event}
                    </li>
                  )}
                  {currentMovement.name_taster && (
                    <li>
                      <strong>Dégustateur:</strong>{" "}
                      {currentMovement.name_taster}
                    </li>
                  )}
                  {currentMovement.name_receiver && (
                    <li>
                      <strong>Destinataire:</strong>{" "}
                      {currentMovement.name_receiver}
                    </li>
                  )}
                  {currentMovement.name_order_by && (
                    <li>
                      <strong>Demandeur:</strong>{" "}
                      {currentMovement.name_order_by}
                    </li>
                  )}
                  {currentMovement.collecteur_produit && (
                    <li>
                      <strong>Collecteur:</strong>{" "}
                      {currentMovement.collecteur_produit}
                    </li>
                  )}
                  {currentMovement.commentaire && (
                    <li>
                      <strong>Commentaire:</strong>{" "}
                      {currentMovement.commentaire}
                    </li>
                  )}
                </ul>
              </div>

              {/* Nouvelles informations de traçabilité */}
              {(currentMovement.name_order_by ||
                currentMovement.collecteur_produit) && (
                <div>
                  <p className="text-lg font-medium">Traçabilité</p>
                  <ul className="p-4 rounded-md bg-blue-50 font-light space-y-1">
                    {currentMovement.name_order_by && (
                      <li>
                        <strong>Demandeur:</strong>{" "}
                        {currentMovement.name_order_by}
                      </li>
                    )}
                    {currentMovement.collecteur_produit && (
                      <li>
                        <strong>Collecteur:</strong>{" "}
                        {currentMovement.collecteur_produit}
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setCurrentMovement(null);
                }}
                className="p-2 px-3 rounded-md bg-blue-500 text-white"
              >
                Fermer ❌
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-center" autoClose={3000} />

      <div className="shadow-md bg-white p-4 sticky top-16 z-10">
        <h1 className="text-2xl font-bold text-gray-900">
          Gestion des Sorties de Stock
        </h1>
        <p className="text-gray-600 mt-1">Transferts et dons d'articles</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-5 gap-8 p-6">
        {/* Formulaire */}
        <ConditionalRender requiredPermissions="canCreateStockOutflow">
          <div className="bg-white rounded-lg shadow-md p-6 xl:col-span-2">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Nouvelle Sortie
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!user.is_caissier && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Magasin émetteur
                  </label>
                  <Select
                    name="magasin"
                    options={state.sellPoints}
                    onChange={handleSelectChange}
                    value={state.sellPoints.find(
                      (opt) => opt.value === formData.magasin
                    )}
                    placeholder="Sélectionnez un magasin"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Article
                </label>
                <Select
                  name="stock_item"
                  options={state.articles}
                  onChange={handleSelectChange}
                  value={state.articles.find(
                    (opt) => opt.value === formData.stock_item
                  )}
                  placeholder={
                    formData.magasin
                      ? "Sélectionnez un article"
                      : "Sélectionnez d'abord un magasin"
                  }
                  isDisabled={!formData.magasin || state.articles.length === 0}
                  required
                  getOptionLabel={(option) =>
                    `${option.label} (Stock: ${option.stock})`
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type de sortie
                </label>
                <Select
                  name="type"
                  options={options.movementTypes}
                  onChange={handleSelectChange}
                  value={options.movementTypes.find(
                    (opt) => opt.value === formData.type
                  )}
                  required
                />
              </div>

              {/* Champ commentaire obligatoire pour tous les types */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Commentaire
                </label>
                <textarea
                  name="commentaire"
                  value={formData.commentaire}
                  onChange={handleChange}
                  placeholder="Ajouter un commentaire"
                  className="w-full px-3 py-2 border rounded-md"
                  required
                  rows="3"
                />
              </div>

              {/* Nouveaux champs pour la traçabilité */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du demandeur de la sortie{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name_order_by"
                    value={formData.name_order_by}
                    onChange={handleChange}
                    placeholder="Ex: AYITE"
                    className="w-full px-3 py-2 border rounded-md focus:ring-amber-500 focus:border-amber-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom de la personne venant chercher le produit{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="collecteur_produit"
                    value={formData.collecteur_produit}
                    onChange={handleChange}
                    placeholder="Nom du collecteur"
                    className="w-full px-3 py-2 border rounded-md focus:ring-amber-500 focus:border-amber-500"
                    required
                  />
                </div>
              </div>

              {/* Champs spécifiques selon le type de sortie */}
              {formData.type === "transfert_store" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Magasin destinataire
                  </label>
                  <Select
                    name="magasin_receiver"
                    options={state.sellPoints.filter(
                      (opt) => opt.value !== formData.magasin
                    )}
                    onChange={handleSelectChange}
                    value={state.sellPoints.find(
                      (opt) => opt.value === formData.magasin_receiver
                    )}
                    placeholder="Sélectionnez un magasin"
                    required
                  />
                </div>
              )}

              {formData.type === "transfert_event" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom de l'événement
                  </label>
                  <input
                    type="text"
                    name="name_event"
                    value={formData.name_event}
                    onChange={handleChange}
                    placeholder="Nom de l'événement"
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
              )}

              {/* {formData.type === "degustation" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du dégustateur
                  </label>
                  <input
                    type="text"
                    name="name_taster"
                    value={formData.name_taster}
                    onChange={handleChange}
                    placeholder="Nom du dégustateur"
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
              )} */}

              {formData.type === "offre" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du destinataire
                  </label>
                  <input
                    type="text"
                    name="name_receiver"
                    value={formData.name_receiver}
                    onChange={handleChange}
                    placeholder="Nom du destinataire de l'offre"
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unité
                  </label>
                  <Select
                    name="unite"
                    options={options.units}
                    onChange={handleSelectChange}
                    value={options.units.find(
                      (opt) => opt.value === formData.unite
                    )}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantité
                    {formData.stock_item && (
                      <span className="text-amber-600 ml-2">
                        {` (Stock disponible: ${
                          state.articles.find(
                            (article) => article.value === formData.stock_item
                          )?.stock || 0
                        })`}
                      </span>
                    )}
                  </label>
                  <NumberInput
                    name="quantite"
                    value={formData.quantite || ""}
                    onChange={(newValue) => {
                      // Récupérer le stock disponible pour l'article sélectionné
                      const selectedArticle = state.articles.find(
                        (article) => article.value === formData.stock_item
                      );

                      // Si aucun article n'est sélectionné ou pas de stock, empêcher la saisie
                      if (!selectedArticle) {
                        toast.warning(
                          "Veuillez d'abord sélectionner un article"
                        );
                        return;
                      }

                      // Vérifier si la valeur dépasse le stock disponible
                      if (newValue && newValue > selectedArticle.stock) {
                        // Limiter la valeur au stock disponible et afficher un message
                        toast.warning(
                          `La quantité ne peut pas dépasser le stock disponible (${selectedArticle.stock})`
                        );
                        setFormData((prev) => ({
                          ...prev,
                          quantite: 0,
                        }));
                      } else {
                        // Sinon, mettre à jour normalement
                        setFormData((prev) => ({
                          ...prev,
                          quantite: newValue,
                        }));
                      }
                    }}
                    placeholder="Quantité"
                    className="w-full px-3 py-2 border rounded-md"
                    required
                    max={
                      state.articles.find(
                        (article) => article.value === formData.stock_item
                      )?.stock || 0
                    }
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={state.isSubmitting || !isFormValid()}
                className={`w-full py-2 px-4 rounded-md text-white ${
                  isFormValid() && !state.isSubmitting
                    ? "bg-amber-600 hover:bg-amber-700"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                {state.isSubmitting ? "Enregistrement..." : "Valider la sortie"}
              </button>
            </form>
          </div>
        </ConditionalRender>

        {/* Historique */}
        <div className="bg-white shadow rounded-lg overflow-hidden xl:col-span-3">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Historique</h2>
          </div>

          <div className="p-6">
            {state.movements.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg
                  className="mx-auto h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="mt-2">Aucun mouvement enregistré</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Article
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Quantité
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Destinataire
                      </th>
                      <th className="sr-only">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {state.movements.map((movement) => (
                      <tr key={movement.id}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {getLocalDateTime(new Date(movement.date_sortie))}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {movement.stock_item_name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                              movement.type === "transfert_store"
                                ? "bg-green-100 text-green-800"
                                : movement.type === "transfert_event"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-pink-100 text-pink-800"
                            }`}
                          >
                            {movement.type === "transfert_store" ? (
                              <TransfertIcon />
                            ) : movement.type === "transfert_event" ? (
                              <EventIcon />
                            ) : (
                              <OfferIcon />
                            )}
                            {movement.type === "transfert_store"
                              ? "Transfert store"
                              : movement.type === "transfert_event"
                              ? "Transfert événement"
                              : "Offre"}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {movement.quantite} {movement.unite}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {movement.magasin_receiver_name ||
                            movement.name_receiver}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 flex justify-end items-center">
                          <button
                            onClick={() => {
                              setCurrentMovement(movement);
                            }}
                            className="px-2 py-1 rounded-md shadow-md bg-blue-500 hover:bg-blue-600 text-white"
                          >
                            Plus >
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default StockOutflowApp;
